import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { formatDate } from '../utils/dates';
import { getExpenses, deleteExpense, getDueRecurringExpenses } from '../api';
import type { RecurringExpense } from '../api';
import type { Expense, ExpenseCategory } from '../types';
import ExpenseModal from '../components/ExpenseModal';
import RecurringExpenseModal from '../components/RecurringExpenseModal';
import RegisterChooserModal from '../components/RegisterChooserModal';
import EventModal from '../components/EventModal';
import type { EventChoice } from '../components/EventModal';
import type { Vehicle } from '../types';
import SortableTh, { type SortDir } from '../components/SortableTh';
import Pagination from '../components/Pagination';

type FilterMode = 'all' | 'fleet';
type SortKey = 'date' | 'vehicle' | 'category' | 'description' | 'amount' | 'vat';

const CATEGORIES: ExpenseCategory[] = ['insurance', 'tax', 'cleaning', 'parking', 'workshop', 'other'];

export default function ExpensesPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [expLoading, setExpLoading] = useState(true);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  // Registrering starter med et type-valg, så en værksted-regning altid bliver en begivenhed
  // og dermed havner i bilens historik i stedet for kun i regnskabet.
  const [showChooser, setShowChooser] = useState(false);
  const [eventFor, setEventFor] = useState<{ vehicle: Vehicle; choice: EventChoice } | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [copyingExpense, setCopyingExpense] = useState<Expense | null>(null);
  const [confirmDeleteExp, setConfirmDeleteExp] = useState<number | null>(null);
  const [dueRecurring, setDueRecurring] = useState<RecurringExpense[]>([]);
  const [approvingRecurring, setApprovingRecurring] = useState<RecurringExpense | null>(null);
  const [expFilterReg, setExpFilterReg]   = useState('');
  const [expFilterFrom, setExpFilterFrom] = useState('');
  const [expFilterTo, setExpFilterTo]     = useState('');
  const [expFilterCategory, setExpFilterCategory] = useState<'' | ExpenseCategory>('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  // Tilbage til første side når udvalget skifter — ellers står man på en side der ikke findes mere
  useEffect(() => { setPage(1); }, [filter, expFilterReg, expFilterFrom, expFilterTo, expFilterCategory]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const loadExpenses = useCallback(async () => {
    setExpLoading(true);
    const params = filter === 'fleet' ? { fleet: true } : {};
    const [exps, due] = await Promise.all([getExpenses(params), getDueRecurringExpenses()]);
    setExpenses(exps);
    setDueRecurring(due);
    setExpLoading(false);
  }, [filter]);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  const handleDeleteExpense = async (id: number) => {
    await deleteExpense(id);
    setConfirmDeleteExp(null);
    loadExpenses();
  };

  const filteredExpenses = expenses.filter(e => {
    if (expFilterReg && !(e.registration_number ?? '').toUpperCase().includes(expFilterReg.toUpperCase())) return false;
    if (expFilterFrom && e.date < expFilterFrom) return false;
    if (expFilterTo   && e.date > expFilterTo)   return false;
    if (expFilterCategory && e.category !== expFilterCategory) return false;
    return true;
  });

  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    let av: string | number = '', bv: string | number = '';
    switch (sortKey) {
      case 'date':        av = a.date;                              bv = b.date; break;
      case 'vehicle':     av = a.registration_number ?? '';         bv = b.registration_number ?? ''; break;
      case 'category':    av = t(`expense.category_${a.category}`); bv = t(`expense.category_${b.category}`); break;
      case 'description': av = a.description ?? '';                 bv = b.description ?? ''; break;
      case 'amount':      av = Number(a.amount);                    bv = Number(b.amount); break;
      case 'vat':         av = a.vat_status;                        bv = b.vat_status; break;
    }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Alle udgifter hentes stadig i ét kald — pagineringen er kun visning, så totalen
  // nedenfor bliver ved med at dække hele det filtrerede udvalg og ikke kun den viste side.
  const expPageSize = settings.itemsPerPage;
  const expTotalPages = Math.max(1, Math.ceil(sortedExpenses.length / expPageSize));
  const expPage = Math.min(page, expTotalPages);  // klemt fast, så en sletning ikke efterlader en tom side
  const pagedExpenses = sortedExpenses.slice((expPage - 1) * expPageSize, expPage * expPageSize);

  const fmt = (n: number) => formatNumber(n, settings.numberFormat);
  const totalAmount = filteredExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const totalVat = filteredExpenses.reduce((sum, e) => {
    if (e.vat_status === 'full') return sum + Number(e.amount) * settings.vatRate / (100 + settings.vatRate);
    if (e.vat_status === 'partial' && e.vat_amount) return sum + Number(e.vat_amount);
    return sum;
  }, 0);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-title">{t('nav.expenses')}</h1>
        <button onClick={() => setShowChooser(true)} className="btn btn-primary">
          + {t('fleet.add_expense')}
        </button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {(['all', 'fleet'] as FilterMode[]).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`btn-toggle ${filter === f ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>
              {t(`fleet.${f === 'all' ? 'all_expenses' : 'fleet_only'}`)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder={t('fleet.filter_reg')}
            value={expFilterReg}
            onChange={e => setExpFilterReg(e.target.value)}
            className="filter-input w-32"
          />
          <select
            value={expFilterCategory}
            onChange={e => setExpFilterCategory(e.target.value as '' | ExpenseCategory)}
            className="filter-input">
            <option value="">{t('expense.all_categories')}</option>
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{t(`expense.category_${c}`)}</option>
            ))}
          </select>
          <input
            type="date"
            value={expFilterFrom}
            onChange={e => setExpFilterFrom(e.target.value)}
            className="filter-input"
          />
          <span className="text-ink-300 text-sm">–</span>
          <input
            type="date"
            value={expFilterTo}
            onChange={e => setExpFilterTo(e.target.value)}
            className="filter-input"
          />
          {(expFilterReg || expFilterFrom || expFilterTo || expFilterCategory) && (
            <button onClick={() => { setExpFilterReg(''); setExpFilterFrom(''); setExpFilterTo(''); setExpFilterCategory(''); }}
              className="text-xs text-ink-500 hover:text-ink-900 ml-1">
              {t('fleet.filter_clear')}
            </button>
          )}
        </div>
      </div>

      <div className="list-card">
        {expLoading ? (
          <div className="p-12 text-center text-ink-500">…</div>
        ) : filteredExpenses.length === 0 && dueRecurring.length === 0 ? (
          <div className="p-12 text-center text-ink-500">{t('fleet.no_expenses')}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh keyName="date"        label={t('fleet.col_date')}        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="vehicle"     label={t('fleet.col_vehicle')}     sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="category"    label={t('fleet.col_category')}    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="description" label={t('fleet.col_description')} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="amount"      label={t('fleet.col_amount')}      sortKey={sortKey} sortDir={sortDir} onSort={handleSort} alignRight />
                <SortableTh keyName="vat"         label={t('fleet.col_vat')}         sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {dueRecurring.map(r => (
                <tr key={`rec-${r.id}`} className="row-warning cursor-pointer"
                  onClick={() => setApprovingRecurring(r)}>
                  <td className="px-4 py-3 text-ink-700 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-[var(--color-warning-500)] flex-shrink-0" />
                      {r.next_due_date}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.registration_number
                      ? <span className="font-mono text-ink-900">{r.registration_number}</span>
                      : <span className="text-xs text-ink-300">{t('expense.scope_fleet')}</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-700">{t(`expense.category_${r.category}`)}</td>
                  <td className="px-4 py-3 text-ink-500 text-xs">{r.description || '—'} <span className="ml-1 text-[var(--color-warning-500)]">({t(`expense.frequency_${r.frequency}`)})</span></td>
                  <td className="px-4 py-3 text-right font-mono text-ink-900">{fmt(Number(r.amount))}</td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 text-right">
                    <span className="text-xs text-sage-700">{t('expense.recurring_approve_btn')}</span>
                  </td>
                </tr>
              ))}
              {pagedExpenses.map(e => (
                <tr key={e.id} className="row-hover">
                  <td className="px-4 py-3 text-ink-500 whitespace-nowrap">{formatDate(e.date)}</td>
                  <td className="px-4 py-3">
                    {e.registration_number
                      ? <span className="font-mono text-ink-900">{e.registration_number}</span>
                      : <span className="text-xs text-ink-300">{t('expense.scope_fleet')}</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-ink-700">{t(`expense.category_${e.category}`)}</td>
                  <td className="px-4 py-3 text-ink-700">{e.description || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-ink-900">{fmt(Number(e.amount))}</td>
                  <td className="px-4 py-3">
                    <span className={`badge badge-vat-${e.vat_status}`}>{t(`vat.${e.vat_status}`)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {confirmDeleteExp === e.id ? (
                      <span className="flex items-center gap-2 justify-end">
                        <button onClick={() => handleDeleteExpense(e.id!)} className="text-xs text-rose-700 hover:underline font-medium">{t('expense.delete_confirm')}</button>
                        <button onClick={() => setConfirmDeleteExp(null)} className="text-xs text-ink-300 hover:text-ink-700">&times;</button>
                      </span>
                    ) : e.maintenance_id ? (
                      <span className="flex items-center gap-3 justify-end">
                        <Link to={`/vehicles/${e.vehicle_id}/history?type=maintenance&id=${e.maintenance_id}`} className="text-xs text-sage-700 hover:text-sage-900">{t('expense.open_event')}</Link>
                      </span>
                    ) : (
                      <span className="flex items-center gap-3 justify-end">
                        <button onClick={() => setEditingExpense(e)} className="text-xs text-sage-700 hover:text-sage-900">{t('events.view_event')}</button>
                        <button onClick={() => setCopyingExpense(e)} className="text-xs text-ink-300 hover:text-sage-700">{t('expense.copy')}</button>
                        <button onClick={() => setConfirmDeleteExp(e.id!)} className="text-xs text-ink-300 hover:text-rose-700">✕</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="px-4 py-3 text-sm font-medium text-ink-700">{t('fleet.total')}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-ink-900">{fmt(totalAmount)}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{t('fleet.total_vat')}: {fmt(totalVat)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
        {!expLoading && expTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 text-sm text-ink-500">
            <span>{t('pagination.showing', {
              from: (expPage - 1) * expPageSize + 1,
              to: Math.min(expPage * expPageSize, sortedExpenses.length),
              total: sortedExpenses.length,
            })}</span>
            <Pagination page={expPage} totalPages={expTotalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {showChooser && (
        <RegisterChooserModal
          onClose={() => setShowChooser(false)}
          onChoose={(choice, v) => {
            setShowChooser(false);
            if (choice === 'expense' || !v) setShowExpenseModal(true);
            else setEventFor({ vehicle: { id: v.id, registration_number: v.registration_number } as Vehicle, choice });
          }}
        />
      )}
      {eventFor && (
        <EventModal
          vehicle={eventFor.vehicle}
          initialChoice={eventFor.choice}
          onSaved={() => { setEventFor(null); loadExpenses(); }}
          onClose={() => setEventFor(null)}
        />
      )}
      {showExpenseModal && (
        <ExpenseModal onSaved={() => { setShowExpenseModal(false); loadExpenses(); }} onClose={() => setShowExpenseModal(false)} />
      )}
      {editingExpense && (
        <ExpenseModal expense={editingExpense} onSaved={() => { setEditingExpense(null); loadExpenses(); }} onClose={() => setEditingExpense(null)} />
      )}
      {copyingExpense && (
        <ExpenseModal
          expense={{ ...copyingExpense, id: undefined }}
          onSaved={() => { setCopyingExpense(null); loadExpenses(); }}
          onClose={() => setCopyingExpense(null)}
        />
      )}
      {approvingRecurring && (
        <RecurringExpenseModal
          recurring={approvingRecurring}
          approveMode
          onSaved={() => { setApprovingRecurring(null); loadExpenses(); }}
          onClose={() => setApprovingRecurring(null)}
        />
      )}
    </main>
  );
}
